/**
 * The blog. One file per post in this directory; register new posts in the
 * list below (order doesn't matter — display order is date desc).
 *
 * Publishing model: a post's `date` is its publish date. Future-dated posts
 * are scheduled — hidden from readers until that local date arrives, visible
 * in dev. See docs/content-playbook.md for the cadence + queue mechanics.
 */
import { estimateReadingTime } from '../../utils/readingTime';
import { localDateKey } from '../../utils/dailyRotation';
import type { BlogPost } from './types';

import { post as beginnersGuide } from './beginners-guide-to-losing-money-with-confidence';
import { post as moneylineHungover } from './moneyline-explained-like-youre-hungover';
import { post as spreadNotScore } from './the-spread-is-not-the-score';
import { post as teasersLoveIsland } from './teasers-for-people-who-cried-at-the-love-island-finale';
import { post as futuresSituationships } from './futures-bets-are-situationships-with-worse-odds';
import { post as parlay101 } from './parlay-101-house-favorite-bet';
import { post as bankrollNoMath } from './bankroll-management-for-people-who-hate-math';
import { post as readingTheLine } from './reading-the-line-spotting-a-trap-game';
import { post as juiceExplained } from './the-juice-explained';
import { post as overUnders } from './over-unders-for-people-with-trust-issues';
import { post as lineShopping } from './line-shopping-the-five-boring-minutes-that-pay';
import { post as playerProps } from './player-props-are-the-reality-tv-of-betting';
import { post as coldStreak } from './the-cold-streak-survival-guide';
import { post as liveBetting } from './live-betting-meet-your-worst-self';
import { post as favoriteTeam } from './why-your-favorite-team-is-your-worst-bet';

export type { BlogPost, Author } from './types';

/** All posts, newest first — INCLUDING scheduled (future-dated) ones. */
export const posts: BlogPost[] = [
  beginnersGuide,
  moneylineHungover,
  spreadNotScore,
  teasersLoveIsland,
  futuresSituationships,
  parlay101,
  bankrollNoMath,
  readingTheLine,
  juiceExplained,
  overUnders,
  lineShopping,
  playerProps,
  coldStreak,
  liveBetting,
  favoriteTeam,
].sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0));

/**
 * Posts visible to readers: date <= today in the visitor's local timezone.
 * Zero-padded ISO keys make the lexicographic compare safe.
 */
export function publishedPosts(today: Date = new Date()): BlogPost[] {
  const key = localDateKey(today);
  return posts.filter((post) => post.date <= key);
}

export function getPostBySlug(
  slug: string | undefined,
  opts: { includeScheduled?: boolean; today?: Date } = {}
): BlogPost | undefined {
  const pool = opts.includeScheduled ? posts : publishedPosts(opts.today);
  return pool.find((post) => post.slug === slug);
}

/** Reading time for a post: explicit override if set, otherwise estimated. */
export function getReadingTime(post: BlogPost): string {
  return post.readingTime ?? estimateReadingTime(post.body);
}
